import { useState, useRef, forwardRef, useImperativeHandle, memo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { toast } from 'react-toastify'

import FileAttachment from './file-attachment'
import { useDragDropFiles } from '../../context/drag-and-drop.context'

import { getPresignedUrl, uploadFileWithProgress } from '@/shared/services/file-upload.service'
import { validateFile } from '@/utils/file-utils'

export type UploadingFile = {
  file: File
  progress: number
  status: 'uploading' | 'success'
  uploadedUrl?: string
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
    border: `1px dashed ${theme.palette.grey[400]}`,
    borderRadius: theme.shape.borderRadius,
  },
}))

export type AttachmentsContainerHandle = {
  getFiles: () => UploadingFile[]
  addFiles: (newFiles: File[]) => void
  updateFileProgress: (file: File, progress: number) => void
  markFileSuccess: (file: File, uploadedUrl: string) => void
  removeFile: (file: File) => void
  reset: () => void
  triggerFileDialog: () => void
}

const AttachmentsContainer = forwardRef<AttachmentsContainerHandle>((_, ref) => {
  const classes = useStyles()
  const [files, setFiles] = useState<UploadingFile[]>([])

  // Extract the file processing logic into a reusable function
  const processFiles = async (newFiles: File[]) => {
    const validFiles: File[] = []

    for (const file of newFiles) {
      const { valid, reason } = validateFile(file)
      if (valid) {
        validFiles.push(file)
      } else {
        toast.error(`Invalid file: ${reason}`)
      }
    }

    if (validFiles.length === 0) {
      return
    }

    const MAX_TOTAL_FILES = 5
    if (files.length + validFiles.length > MAX_TOTAL_FILES) {
      toast.error(`You cannot upload more than ${MAX_TOTAL_FILES} files in total.`)
      return
    }

    addFiles(validFiles)

    for (const file of validFiles) {
      try {
        const presigned = await getPresignedUrl(file)

        const uploadedUrl = await uploadFileWithProgress(file, presigned, (pct) => {
          updateFileProgress(file, pct)
        })

        markFileSuccess(file, uploadedUrl)
      } catch (err) {
        removeFile(file)
        console.error('Error uploading file:', err)
        toast.error('Error uploading file')
      }
    }
  }

  // Register the file processor with the drag & drop system
  useDragDropFiles(processFiles)

  const addFiles = (newFiles: File[]) => {
    const toAdd: UploadingFile[] = newFiles.map((file) => ({
      file,
      progress: 0,
      status: 'uploading',
    }))
    setFiles((prev) => [...prev, ...toAdd])
  }

  const removeFile = (file: File) => {
    setFiles((prev) => prev.filter((f) => f.file !== file))
  }

  const updateFileProgress = (file: File, progress: number) => {
    setFiles((prev) => prev.map((f) => (f.file === file ? { ...f, progress } : f)))
  }

  const markFileSuccess = (file: File, uploadedUrl: string) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.file === file ? { ...f, status: 'success', progress: 100, uploadedUrl } : f
      )
    )
  }

  useImperativeHandle(ref, () => ({
    getFiles() {
      return files
    },
    addFiles: processFiles, // Use the processing function for imperative handle too
    updateFileProgress,
    markFileSuccess,
    removeFile,
    reset: () => setFiles([]),
    triggerFileDialog: () => {
      inputRef.current?.click()
    },
  }))

  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const filesFromInput = e.target.files
    if (!filesFromInput) return

    await processFiles(Array.from(filesFromInput))

    if (e.target) e.target.value = ''
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileInput}
      />

      {files.length > 0 && (
        <div className={classes.container}>
          {files.map((upload, idx) => (
            <FileAttachment
              key={idx}
              filename={upload.file.name}
              extension={getExtension(upload.file.name)}
              progress={upload.progress}
              status={upload.status}
              onRemove={() => {
                removeFile(upload.file)
              }}
            />
          ))}
        </div>
      )}
    </>
  )
})

AttachmentsContainer.displayName = 'AttachmentsContainer'

export default memo(AttachmentsContainer)

const getExtension = (filename: string) => {
  const parts = filename.split('.')
  return parts.length > 1 ? parts[parts.length - 1] : ''
}
